import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { format, isToday, isThisWeek, parseISO } from 'date-fns';
import SyndicationWidget from '../components/SyndicationWidget';
import CrossPlatformPanel from '../components/CrossPlatformPanel';
import { 
  Users, 
  UserPlus,
  Sparkles,
  DollarSign, 
  Calendar, 
  TrendingUp,
  TrendingDown,
  ChevronRight,
  FileText,
  Send,
  Package,
  Clock,
  CheckCircle2,
  AlertCircle,
  Target,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
  CalendarDays,
  Settings,
  BookOpen,
  Eye,
  MessageSquare,
  Star,
  Zap,
  Activity,
  ShoppingBag,
  Users2,
  Mail,
  Loader2,
  CreditCard,
  CircleDollarSign,
  ExternalLink,
  Bell,
  Check,
  Image,
  Plus,
  Trash2,
  GripVertical,
  ArrowUp,
  ArrowDown,
  X,
  EyeOff,
  Warehouse,
  Building,
  Globe,
  Layout as LayoutIcon,
  RotateCcw,
  Search
} from 'lucide-react';
import PlatformKPIs from '../components/admin/PlatformKPIs';
import TicketTransactionsCard from '../components/admin/TicketTransactionsCard';
import ServicesDirectory from '../components/admin/ServicesDirectory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-scoped layout defaults (stable references, used by SectionShell deps).
const DEFAULT_TOP = ['platform_kpis', 'events_pulse', 'tasks_across_events', 'event_syndication', 'departments'];
const DEFAULT_LEFT = ['ticket_transactions', 'quick_actions', 'payment_plans', 'sales_pipeline', 'workspaces'];
const DEFAULT_RIGHT = ['services_directory', 'business_health', 'recent_bookings', 'blog_content', 'email_digests', 'quick_links'];

// Hoisted outside the component so each dashboard render doesn't create a
// new SectionShell type (which would unmount/remount the 10 section subtrees).
const SECTION_LABELS = {
  platform_kpis: 'Platform Performance',
  event_syndication: 'Event Syndication',
  events_pulse: 'Events Pulse',
  tasks_across_events: 'Tasks Across All Events',
  departments: 'Departments at a glance',
  ticket_transactions: 'Ticket Transactions',
  quick_actions: 'Quick Actions',
  payment_plans: 'Payment Plans',
  sales_pipeline: 'Sales Pipeline',
  workspaces: 'Active Workspaces',
  services_directory: 'All Services',
  business_health: 'Business Health',
  recent_bookings: 'Recent Bookings',
  blog_content: 'Blog & Content',
  email_digests: 'Email Digests',
  quick_links: 'Manage (Quick Links)',
};

function SectionShell({
  sectionKey,
  column,
  layout,
  editMode,
  dragInfo,
  dragOverKey,
  onSectionDragStart,
  onSectionDragOver,
  setDragOverKey,
  onSectionDrop,
  setDragInfo,
  toggleSectionHidden,
  children,
}) {
  const colArr = column === 'top' ? layout.top : (column === 'left' ? layout.left : layout.right);
  const order = (colArr || []).indexOf(sectionKey);
  const isHidden = layout.hidden.includes(sectionKey);
  if (isHidden && !editMode) return null;
  const isDragging = dragInfo?.key === sectionKey;
  const isDropTarget = dragOverKey === sectionKey && dragInfo && dragInfo.key !== sectionKey;
  return (
    <div
      style={{ order: order < 0 ? 999 : order }}
      className={`relative group ${isDragging ? 'opacity-40' : ''} ${isDropTarget ? 'ring-2 ring-indigo-500 rounded-xl' : ''} ${isHidden ? 'opacity-50' : ''}`}
      draggable={editMode}
      onDragStart={editMode ? onSectionDragStart(sectionKey, column) : undefined}
      onDragOver={editMode ? onSectionDragOver(sectionKey) : undefined}
      onDragLeave={editMode ? () => setDragOverKey(null) : undefined}
      onDrop={editMode ? onSectionDrop(sectionKey, column) : undefined}
      onDragEnd={editMode ? () => { setDragInfo(null); setDragOverKey(null); } : undefined}
      data-testid={`section-${sectionKey}`}
    >
      {editMode && (
        <div className="absolute -top-2 left-2 z-10 flex items-center gap-1 bg-indigo-500 text-white text-xs font-medium px-2 py-1 rounded-md shadow-lg cursor-move select-none" data-testid={`section-drag-${sectionKey}`}>
          <GripVertical className="h-3.5 w-3.5" />
          <span>{SECTION_LABELS[sectionKey] || sectionKey}</span>
          <button
            onClick={(e) => { e.stopPropagation(); toggleSectionHidden(sectionKey); }}
            className="ml-1 hover:bg-indigo-600 rounded p-0.5"
            title={isHidden ? 'Show section' : 'Hide section'}
            data-testid={`section-toggle-${sectionKey}`}
          >
            {isHidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </button>
        </div>
      )}
      {children}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    // Revenue & Financial
    totalRevenue: 0,
    monthlyRevenue: 0,
    pendingPayments: 0,
    revenueGrowth: 0,
    // Bookings
    totalBookings: 0,
    activeBookings: 0,
    upcomingEvents: 0,
    completedEvents: 0,
    // Pipeline
    totalLeads: 0,
    newLeads: 0,
    proposalsSent: 0,
    proposalsAccepted: 0,
    conversionRate: 0,
    pipelineValue: 0,
    // Workspaces
    activeWorkspaces: 0,
    tasksPending: 0,
    tasksOverdue: 0,
    // Blog
    publishedPosts: 0,
    draftPosts: 0,
    totalViews: 0,
    // Recent Activity
    recentBookings: [],
    recentProposals: [],
    upcomingTasks: [],
    todayEvents: []
  });
  const [systemErrors, setSystemErrors] = useState({ errors: [], unresolved: 0 });
  const [digestSettings, setDigestSettings] = useState({ enabled: false, frequency: 'daily', last_sent_at: null });
  const [digestSending, setDigestSending] = useState(false);
  const [paymentPlansData, setPaymentPlansData] = useState(null);
  const [reminderSending, setReminderSending] = useState(false);
  const [pendingVenues, setPendingVenues] = useState([]);

  // Shared cross-platform data (events_pulse, tasks, departments) — fetched
  // once and passed to all 3 CrossPlatformPanel sections so they don't
  // each refetch the same payload.
  const [crossPlatformData, setCrossPlatformData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/dashboard/cross-platform`, { headers: { Authorization: `Bearer ${token}` } });
        setCrossPlatformData(res.data);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // --- dashboard layout customization (drag-and-drop per admin) -----------
  const [layout, setLayout] = useState({ top: DEFAULT_TOP, left: DEFAULT_LEFT, right: DEFAULT_RIGHT, hidden: [] });
  const [editMode, setEditMode] = useState(false);
  const [dragInfo, setDragInfo] = useState(null); // { key, column }
  const [dragOverKey, setDragOverKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/dashboard/layout`, { headers: { Authorization: `Bearer ${token}` } });
        setLayout({
          top: res.data.top || DEFAULT_TOP,
          left: res.data.left || DEFAULT_LEFT,
          right: res.data.right || DEFAULT_RIGHT,
          hidden: res.data.hidden || [],
        });
      } catch (err) { /* keep defaults */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const persistLayout = async (next) => {
    setLayout(next);
    try {
      const res = await axios.put(`${API}/admin/dashboard/layout`, next, { headers: { Authorization: `Bearer ${token}` } });
      // Re-hydrate from sanitized server response in case the backend dropped
      // or re-appended any keys.
      setLayout({ top: res.data.top, left: res.data.left, right: res.data.right, hidden: res.data.hidden });
    } catch (err) { toast.error('Failed to save layout'); }
  };

  const resetLayout = async () => {
    try {
      const res = await axios.post(`${API}/admin/dashboard/layout/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLayout({ top: res.data.top, left: res.data.left, right: res.data.right, hidden: res.data.hidden });
      toast.success('Layout reset to default');
    } catch (err) { toast.error('Failed to reset layout'); }
  };

  const onSectionDragStart = (key, column) => (e) => {
    setDragInfo({ key, column });
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox
    try { e.dataTransfer.setData('text/plain', key); } catch { /* noop */ }
  };

  const onSectionDragOver = (key) => (e) => {
    e.preventDefault();
    if (dragInfo && dragInfo.key !== key) setDragOverKey(key);
    e.dataTransfer.dropEffect = 'move';
  };

  const onSectionDrop = (targetKey, targetColumn) => (e) => {
    e.preventDefault();
    setDragOverKey(null);
    if (!dragInfo || (dragInfo.key === targetKey && dragInfo.column === targetColumn)) {
      setDragInfo(null);
      return;
    }
    // Only allow reordering within the same column.
    if (dragInfo.column !== targetColumn) {
      setDragInfo(null);
      toast.info('Sections can only be reordered within their column');
      return;
    }
    const src = dragInfo;
    setDragInfo(null);
    const nextTop = [...(layout.top || [])];
    const nextLeft = [...layout.left];
    const nextRight = [...layout.right];
    const arr = targetColumn === 'top' ? nextTop : (targetColumn === 'left' ? nextLeft : nextRight);
    const srcIdx = arr.indexOf(src.key);
    if (srcIdx >= 0) arr.splice(srcIdx, 1);
    const targetIdx = arr.indexOf(targetKey);
    if (targetIdx < 0) arr.push(src.key);
    else arr.splice(targetIdx, 0, src.key);
    persistLayout({ top: nextTop, left: nextLeft, right: nextRight, hidden: layout.hidden });
  };

  const toggleSectionHidden = (key) => {
    const hidden = layout.hidden.includes(key)
      ? layout.hidden.filter(k => k !== key)
      : [...layout.hidden, key];
    persistLayout({ ...layout, hidden });
  };

  // Props forwarded to each hoisted SectionShell instance
  const shellProps = {
    layout, editMode, dragInfo, dragOverKey,
    onSectionDragStart, onSectionDragOver, setDragOverKey,
    onSectionDrop, setDragInfo, toggleSectionHidden,
  };


  useEffect(() => {
    fetchDashboardData();
    fetchSystemErrors();
    fetchDigestSettings();
    fetchPaymentPlansOverview();
    fetchPendingVenues();
  }, []);

  const fetchDigestSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/digest/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setDigestSettings(res.data);
    } catch (err) { console.error('Digest settings fetch error:', err); }
  };

  const updateDigestSettings = async (updates) => {
    try {
      const res = await axios.put(`${API}/admin/digest/settings`, updates, { headers: { Authorization: `Bearer ${token}` } });
      setDigestSettings(res.data);
      toast.success('Digest settings updated');
    } catch (err) { toast.error('Failed to update digest settings'); }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const res = await axios.post(`${API}/admin/digest/send-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Digest sent!');
      fetchDigestSettings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send digest'); }
    finally { setDigestSending(false); }
  };

  const fetchSystemErrors = async () => {
    try {
      const res = await axios.get(`${API}/admin/system-errors?resolved=false&limit=10`, { headers: { Authorization: `Bearer ${token}` } });
      setSystemErrors(res.data);
    } catch (err) {
      console.error('Failed to fetch system errors:', err);
    }
  };

  const fetchPaymentPlansOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/payment-plans-overview`, { headers: { Authorization: `Bearer ${token}` } });
      setPaymentPlansData(res.data);
    } catch (err) {
      console.error('Failed to fetch payment plans:', err);
    }
  };

  const sendPaymentReminders = async () => {
    setReminderSending(true);
    try {
      const res = await axios.post(`${API}/admin/payment-plans/send-reminders`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sent ${res.data.reminders_sent} reminder(s), ${res.data.skipped} skipped`);
      fetchPaymentPlansOverview();
    } catch (err) {
      toast.error('Failed to send reminders');
    } finally {
      setReminderSending(false);
    }
  };

  const resolveError = async (errorId) => {
    try {
      await axios.put(`${API}/admin/system-errors/${errorId}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Error marked as resolved');
      fetchSystemErrors();
    } catch (err) {
      toast.error('Failed to resolve error');
    }
  };

  const resolveAllErrors = async () => {
    try {
      await axios.put(`${API}/admin/system-errors/resolve-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('All errors resolved');
      fetchSystemErrors();
    } catch (err) {
      toast.error('Failed to resolve errors');
    }
  };

  const fetchPendingVenues = async () => {
    try {
      const res = await axios.get(`${API}/admin/venues/pending`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingVenues(res.data.venues || []);
    } catch (err) { console.error('Pending venues fetch error:', err); }
  };

  const approveVenue = async (venueId) => {
    try {
      await axios.put(`${API}/admin/venues/${venueId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Venue approved and now live!');
      fetchPendingVenues();
    } catch (err) { toast.error('Failed to approve venue'); }
  };

  const rejectVenue = async (venueId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    try {
      await axios.put(`${API}/admin/venues/${venueId}/reject`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Venue rejected');
      fetchPendingVenues();
    } catch (err) { toast.error('Failed to reject venue'); }
  };

  const fetchDashboardData = async () => {
    try {
      const [
        statsRes,
        leadsRes,
        proposalsRes,
        bookingsRes,
        workspacesRes,
        blogsRes,
        eventsRes
      ] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pipeline/proposals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/quotes/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/workspaces`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { workspaces: [] } })),
        axios.get(`${API}/blogs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/events`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);

      const stats = statsRes.data;
      const leads = leadsRes.data || [];
      const proposals = proposalsRes.data || [];
      const bookings = bookingsRes.data || [];
      const workspaces = workspacesRes.data?.workspaces || workspacesRes.data || [];
      const blogs = blogsRes.data?.blogs || blogsRes.data || [];
      const events = eventsRes.data?.events || eventsRes.data || [];

      // Calculate metrics
      const acceptedProposals = proposals.filter(p => p.status === 'accepted');
      const sentProposals = proposals.filter(p => ['sent', 'viewed', 'pending', 'accepted', 'declined'].includes(p.status));
      const pipelineValue = proposals
        .filter(p => ['sent', 'viewed', 'pending'].includes(p.status))
        .reduce((sum, p) => sum + (p.total || 0), 0);

      // Active workspaces with pending tasks
      const activeWs = workspaces.filter(w => w.status !== 'completed');
      const allTasks = workspaces.flatMap(w => w.tasks || []);
      const pendingTasks = allTasks.filter(t => t.status !== 'completed');
      const overdueTasks = pendingTasks.filter(t => {
        if (!t.due_date) return false;
        return new Date(t.due_date) < new Date();
      });

      // Today's events
      const todayEvents = events.filter(e => {
        if (!e.event_date) return false;
        return isToday(parseISO(e.event_date));
      });

      // Upcoming events (next 7 days)
      const upcoming = events.filter(e => {
        if (!e.event_date) return false;
        const eventDate = parseISO(e.event_date);
        return eventDate > new Date() && isThisWeek(eventDate);
      });

      // Recent bookings (last 5)
      const recentBookings = bookings
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      // Recent proposals (last 5)
      const recentProposals = proposals
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setDashboardData({
        // Revenue
        totalRevenue: stats?.total_revenue || 0,
        monthlyRevenue: stats?.monthly_revenue || stats?.total_revenue * 0.3 || 0,
        pendingPayments: bookings.filter(b => b.payment_status === 'pending').reduce((s, b) => s + (b.total || 0), 0),
        revenueGrowth: 12.5, // Mock for now
        // Bookings
        totalBookings: bookings.length,
        activeBookings: bookings.filter(b => b.status === 'confirmed').length,
        upcomingEvents: upcoming.length,
        completedEvents: events.filter(e => e.status === 'completed').length,
        // Pipeline
        totalLeads: leads.length,
        newLeads: leads.filter(l => l.status === 'new').length,
        proposalsSent: sentProposals.length,
        proposalsAccepted: acceptedProposals.length,
        conversionRate: sentProposals.length > 0 ? (acceptedProposals.length / sentProposals.length * 100) : 0,
        pipelineValue: pipelineValue,
        // Workspaces
        activeWorkspaces: activeWs.length,
        tasksPending: pendingTasks.length,
        tasksOverdue: overdueTasks.length,
        // Blog
        publishedPosts: blogs.filter(b => b.is_published || b.status === 'published').length,
        draftPosts: blogs.filter(b => !b.is_published && b.status !== 'published').length,
        totalViews: blogs.reduce((s, b) => s + (b.view_count || 0), 0),
        // Activity
        recentBookings,
        recentProposals,
        upcomingTasks: pendingTasks.slice(0, 5),
        todayEvents
      });
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const QuickActionCard = ({ icon: Icon, title, description, onClick, color = "indigo" }) => (
    <Card 
      className="bg-card border-border hover:border-indigo-500/50 transition-all cursor-pointer group"
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-3 rounded-xl bg-${color}-500/10 group-hover:bg-${color}-500/20 transition-colors`}>
          <Icon className={`h-6 w-6 text-${color}-500`} />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-foreground">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
      </CardContent>
    </Card>
  );

  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "slate" }) => (
    <Card className="bg-card border-border">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-2xl font-bold text-foreground">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-${color}-500/10`}>
            <Icon className={`h-5 w-5 text-${color}-500`} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-3">
            {trend === 'up' ? (
              <ArrowUpRight className="h-4 w-4 text-emerald-500" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-red-500" />
            )}
            <span className={`text-sm font-medium ${trend === 'up' ? 'text-emerald-500' : 'text-red-500'}`}>
              {trendValue}
            </span>
            <span className="text-sm text-muted-foreground">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background pt-20 pb-12" data-testid="admin-dashboard">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground mb-1">
              Welcome back, {user?.name?.split(' ')[0] || 'Admin'}
            </h1>
            <p className="text-muted-foreground">
              Your platform command center — revenue, transactions and every service in one place
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant={editMode ? 'default' : 'outline'} onClick={() => setEditMode(v => !v)} data-testid="customize-layout-toggle">
              <LayoutIcon className="h-4 w-4 mr-2" />
              {editMode ? 'Done' : 'Customize Layout'}
            </Button>
            {editMode && (
              <Button variant="outline" onClick={resetLayout} data-testid="reset-layout-btn">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/admin/settings')}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button className="bg-indigo-500 hover:bg-indigo-600" onClick={() => navigate('/admin/proposals/new')}>
              <Send className="h-4 w-4 mr-2" />
              New Proposal
            </Button>
          </div>
        </div>

        {/* System Error Alerts */}
        {systemErrors.unresolved > 0 && (
          <Card className="mb-6 border-red-500/30 bg-red-500/5" data-testid="system-error-alert">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="font-semibold text-red-500">{systemErrors.unresolved} System Error{systemErrors.unresolved > 1 ? 's' : ''} Detected</span>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={resolveAllErrors} data-testid="resolve-all-errors">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve All
                </Button>
              </div>
              <div className="space-y-2">
                {systemErrors.errors.slice(0, 5).map(err => (
                  <div key={err.error_id} className="flex items-center justify-between p-2.5 rounded-lg bg-red-500/5 border border-red-500/10" data-testid={`error-${err.error_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">{err.method} {err.status_code}</Badge>
                        <span className="text-sm font-medium text-foreground truncate">{err.path}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{err.error_message}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(err.created_at).toLocaleString()}</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs ml-3 flex-shrink-0" onClick={() => resolveError(err.error_id)} data-testid={`resolve-${err.error_id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top column: drag-to-reorder full-width sections */}
        <div className="flex flex-col gap-6 mb-6">
          <SectionShell sectionKey="platform_kpis" column="top" {...shellProps}>
            <PlatformKPIs token={token} />
          </SectionShell>
          <SectionShell sectionKey="events_pulse" column="top" {...shellProps}>
            <CrossPlatformPanel token={token} section="events_pulse" data={crossPlatformData} externallyManaged />
          </SectionShell>
          <SectionShell sectionKey="tasks_across_events" column="top" {...shellProps}>
            <CrossPlatformPanel token={token} section="tasks_across_events" data={crossPlatformData} externallyManaged />
          </SectionShell>
          <SectionShell sectionKey="event_syndication" column="top" {...shellProps}>
            <div>
              <SyndicationWidget token={token} />
            </div>
          </SectionShell>
          <SectionShell sectionKey="departments" column="top" {...shellProps}>
            <CrossPlatformPanel token={token} section="departments" data={crossPlatformData} externallyManaged />
          </SectionShell>
        </div>

        {/* Today's Alerts */}
        {(dashboardData.todayEvents.length > 0 || dashboardData.tasksOverdue > 0) && (
          <div className="mb-6 space-y-2">
            {dashboardData.todayEvents.length > 0 && (
              <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-indigo-500" />
                <span className="font-medium text-indigo-700 dark:text-indigo-300">
                  You have {dashboardData.todayEvents.length} event{dashboardData.todayEvents.length > 1 ? 's' : ''} today
                </span>
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => navigate('/admin/events')}>
                  View Events
                </Button>
              </div>
            )}            {dashboardData.tasksOverdue > 0 && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <span className="font-medium text-amber-700 dark:text-amber-300">
                  {dashboardData.tasksOverdue} overdue task{dashboardData.tasksOverdue > 1 ? 's' : ''} need attention
                </span>
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => navigate('/admin/workspaces')}>
                  View Tasks
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Main KPI Grid */}

        {/* Pending Venue Approvals */}
        {pendingVenues.length > 0 && (
          <Card className="mb-6 border-orange-500/30 bg-orange-500/5" data-testid="pending-venues-alert">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-orange-500" />
                  <span className="font-semibold text-orange-500">{pendingVenues.length} Venue{pendingVenues.length > 1 ? 's' : ''} Pending Review</span>
                </div>
              </div>
              <div className="space-y-3">
                {pendingVenues.slice(0, 5).map(v => (
                  <div key={v.venue_id} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border" data-testid={`pending-venue-${v.venue_id}`}>
                    <div>
                      <p className="font-medium text-sm text-foreground">{v.name}</p>
                      <p className="text-xs text-muted-foreground">{v.address?.city}, {v.address?.state} — Submitted by {v.owner_name || 'Unknown'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={() => rejectVenue(v.venue_id)} data-testid={`reject-venue-${v.venue_id}`}>
                        Reject
                      </Button>
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => approveVenue(v.venue_id)} data-testid={`approve-venue-${v.venue_id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard 
            title="Total Revenue" 
            value={`$${dashboardData.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            subtitle={`$${dashboardData.pendingPayments.toFixed(0)} pending`}
            icon={DollarSign}
            trend="up"
            trendValue={`${dashboardData.revenueGrowth}%`}
            color="emerald"
          />
          <MetricCard 
            title="Active Bookings" 
            value={dashboardData.activeBookings}
            subtitle={`${dashboardData.upcomingEvents} upcoming this week`}
            icon={Calendar}
            color="blue"
          />
          <MetricCard 
            title="Pipeline Value" 
            value={`$${dashboardData.pipelineValue.toLocaleString('en-US', { minimumFractionDigits: 0 })}`}
            subtitle={`${dashboardData.proposalsSent} proposals in pipeline`}
            icon={Target}
            color="purple"
          />
          <MetricCard 
            title="Conversion Rate" 
            value={`${dashboardData.conversionRate.toFixed(1)}%`}
            subtitle={`${dashboardData.proposalsAccepted} of ${dashboardData.proposalsSent} converted`}
            icon={TrendingUp}
            color="amber"
          />
        </div>

        {/* Two Column Layout — sections are drag-to-reorder within each column */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          
          {/* Left Column - Quick Actions & Pipeline */}
          <div className="lg:col-span-2 flex flex-col gap-6">

            {/* Platform-wide ticket transactions */}
            <SectionShell sectionKey="ticket_transactions" column="left" {...shellProps}>
              <TicketTransactionsCard token={token} />
            </SectionShell>

            {/* Quick Actions */}
            <SectionShell sectionKey="quick_actions" column="left" {...shellProps}>
            <Card className="bg-card border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-500" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-3">
                <QuickActionCard 
                  icon={Send} 
                  title="Create Proposal" 
                  description="Send a new proposal to a client"
                  onClick={() => navigate('/admin/proposals/new')}
                  color="indigo"
                />
                <QuickActionCard 
                  icon={Briefcase} 
                  title="View Workspaces" 
                  description={`${dashboardData.activeWorkspaces} active projects`}
                  onClick={() => navigate('/admin/workspaces')}
                  color="blue"
                />
                <QuickActionCard 
                  icon={Target} 
                  title="Sales Pipeline" 
                  description={`${dashboardData.newLeads} new leads`}
                  onClick={() => navigate('/admin/pipeline')}
                  color="purple"
                />
                <QuickActionCard 
                  icon={BarChart3} 
                  title="Analytics" 
                  description="View detailed reports"
                  onClick={() => navigate('/admin/analytics')}
                  color="emerald"
                />
                <QuickActionCard 
                  icon={ShoppingBag} 
                  title="Marketplace" 
                  description="Browse & quick-quote"
                  onClick={() => navigate('/marketplace')}
                  color="amber"
                />
                <QuickActionCard 
                  icon={Users2} 
                  title="Eventism Staffing" 
                  description="Post & manage staffing jobs"
                  onClick={() => navigate('/staffing')}
                  color="rose"
                />
                <QuickActionCard 
                  icon={Package} 
                  title="Products" 
                  description="Manage service catalog"
                  onClick={() => navigate('/admin/products')}
                  color="teal"
                />
                <QuickActionCard 
                  icon={Zap} 
                  title="Marketing" 
                  description="AI landing & sales pages"
                  onClick={() => navigate('/admin/marketing')}
                  color="violet"
                />
                <QuickActionCard 
                  icon={MessageSquare} 
                  title="CRM" 
                  description="Manage all relationships"
                  onClick={() => navigate('/admin/crm')}
                  color="indigo"
                />
                <QuickActionCard 
                  icon={Warehouse} 
                  title="Supplier Hub & Calendar" 
                  description="Manage services, bookings & Google Calendar"
                  onClick={() => navigate('/vendor')}
                  color="cyan"
                />
                <QuickActionCard 
                  icon={Building} 
                  title="Venue Partners" 
                  description={`${pendingVenues.length} pending review`}
                  onClick={() => navigate('/admin/venues')}
                  color="orange"
                />
                <QuickActionCard 
                  icon={Globe} 
                  title="Market Expansion" 
                  description="3 active markets"
                  onClick={() => navigate('/admin/markets')}
                  color="violet"
                />
                <QuickActionCard
                  icon={Search}
                  title="SEO Indexing"
                  description="Google + Bing + Yandex"
                  onClick={() => navigate('/admin/indexing')}
                  color="indigo"
                />
              </CardContent>
            </Card>
            </SectionShell>

            {/* Payment Plans Overview */}
            <SectionShell sectionKey="payment_plans" column="left" {...shellProps}>
            {paymentPlansData && (
              <Card className="bg-card border-border" data-testid="payment-plans-overview">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CircleDollarSign className="h-5 w-5 text-indigo-500" />
                    Payment Plans
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={sendPaymentReminders} disabled={reminderSending} data-testid="send-reminders-btn">
                      {reminderSending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Bell className="h-3.5 w-3.5 mr-1" />}
                      Send Reminders
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* KPI Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="text-center p-3 bg-slate-50 dark:bg-card rounded-lg border">
                      <p className="text-2xl font-bold text-foreground">{paymentPlansData.summary.active_plans}</p>
                      <p className="text-xs text-muted-foreground">Active Plans</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      <p className="text-2xl font-bold text-emerald-600">${paymentPlansData.summary.total_collected.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
                      <p className="text-xs text-muted-foreground">Collected</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-2xl font-bold text-amber-600">${paymentPlansData.summary.total_outstanding.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
                      <p className="text-xs text-muted-foreground">Outstanding</p>
                    </div>
                    <div className={`text-center p-3 rounded-lg border ${paymentPlansData.summary.overdue_installments > 0 ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800' : 'bg-slate-50 dark:bg-card'}`}>
                      <p className={`text-2xl font-bold ${paymentPlansData.summary.overdue_installments > 0 ? 'text-red-600' : 'text-foreground'}`}>{paymentPlansData.summary.overdue_installments}</p>
                      <p className="text-xs text-muted-foreground">Overdue</p>
                    </div>
                  </div>

                  {/* Extra stats row */}
                  <div className="flex gap-4 text-xs text-muted-foreground mb-4 px-1">
                    <span>{paymentPlansData.summary.completed_plans} completed</span>
                    <span>{paymentPlansData.summary.deposit_pending} deposits pending</span>
                    <span>{paymentPlansData.summary.auto_charge_plans} auto-pay</span>
                  </div>

                  {/* Plans List */}
                  <Separator className="my-3" />
                  <p className="text-sm font-medium text-muted-foreground mb-3">Active Plans</p>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {paymentPlansData.plans.filter(p => p.status === 'active').slice(0, 8).map((plan) => (
                      <div 
                        key={plan.plan_id} 
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-card rounded-lg hover:bg-slate-100 dark:hover:bg-muted cursor-pointer transition-colors border"
                        onClick={() => navigate(`/invoice/${plan.quote_id}`)}
                        data-testid={`plan-row-${plan.plan_id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            plan.overdue_count > 0 ? 'bg-red-500' : 
                            plan.deposit_status === 'paid' ? 'bg-emerald-500' : 'bg-amber-500'
                          }`} />
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-foreground truncate">{plan.customer_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {plan.event_date ? new Date(plan.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'TBD'}
                              {' · '}{plan.paid_count}/{plan.installments_count} payments
                              {plan.overdue_count > 0 && <span className="text-red-500 ml-1">({plan.overdue_count} overdue)</span>}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <p className="font-semibold text-sm text-foreground">${plan.balance.toFixed(0)}</p>
                          <p className="text-[10px] text-muted-foreground">remaining</p>
                        </div>
                      </div>
                    ))}
                    {paymentPlansData.plans.filter(p => p.status === 'active').length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No active payment plans</p>
                    )}
                  </div>

                  {/* Overdue Alert */}
                  {paymentPlansData.summary.overdue_amount > 0 && (
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700 dark:text-red-400">
                        <strong>${paymentPlansData.summary.overdue_amount.toFixed(2)}</strong> overdue across {paymentPlansData.summary.overdue_installments} installment(s)
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            </SectionShell>

            {/* Pipeline Overview */}
            <SectionShell sectionKey="sales_pipeline" column="left" {...shellProps}>
            <Card className="bg-card border-border">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-500" />
                  Sales Pipeline
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/pipeline')}>
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {[
                    { label: 'New Leads', value: dashboardData.newLeads, color: 'bg-blue-500' },
                    { label: 'Contacted', value: dashboardData.totalLeads - dashboardData.newLeads, color: 'bg-purple-500' },
                    { label: 'Proposals', value: dashboardData.proposalsSent, color: 'bg-amber-500' },
                    { label: 'Won', value: dashboardData.proposalsAccepted, color: 'bg-emerald-500' },
                    { label: 'Lost', value: 0, color: 'bg-red-500' },
                  ].map((stage, idx) => (
                    <div key={idx} className="text-center">
                      <div className={`h-2 rounded-full ${stage.color} mb-2`} />
                      <p className="text-xl font-bold text-foreground">{stage.value}</p>
                      <p className="text-xs text-muted-foreground">{stage.label}</p>
                    </div>
                  ))}
                </div>
                
                {/* Recent Proposals */}
                <Separator className="my-4" />
                <p className="text-sm font-medium text-muted-foreground mb-3">Recent Proposals</p>
                <div className="space-y-2">
                  {dashboardData.recentProposals.slice(0, 3).map((proposal, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-card rounded-lg hover:bg-slate-100 dark:hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/proposals/${proposal.proposal_id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          proposal.status === 'accepted' ? 'bg-emerald-500' :
                          proposal.status === 'declined' ? 'bg-red-500' :
                          proposal.status === 'viewed' ? 'bg-amber-500' :
                          'bg-blue-500'
                        }`} />
                        <div>
                          <p className="font-medium text-sm text-foreground">{proposal.client_name}</p>
                          <p className="text-xs text-muted-foreground">{proposal.proposal_number || proposal.proposal_id}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm text-foreground">${(proposal.total || 0).toFixed(0)}</p>
                        <Badge variant="outline" className="text-xs">
                          {proposal.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            </SectionShell>

            {/* Workspaces & Tasks */}
            <SectionShell sectionKey="workspaces" column="left" {...shellProps}>
            <Card className="bg-card border-border">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-500" />
                  Active Workspaces
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/workspaces')}>
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-slate-50 dark:bg-card rounded-lg">
                    <p className="text-2xl font-bold text-foreground">{dashboardData.activeWorkspaces}</p>
                    <p className="text-xs text-muted-foreground">Active Projects</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 dark:bg-card rounded-lg">
                    <p className="text-2xl font-bold text-foreground">{dashboardData.tasksPending}</p>
                    <p className="text-xs text-muted-foreground">Pending Tasks</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{dashboardData.tasksOverdue}</p>
                    <p className="text-xs text-muted-foreground">Overdue</p>
                  </div>
                </div>
                
                {dashboardData.upcomingTasks.length > 0 && (
                  <>
                    <Separator className="my-4" />
                    <p className="text-sm font-medium text-muted-foreground mb-3">Upcoming Tasks</p>
                    <div className="space-y-2">
                      {dashboardData.upcomingTasks.slice(0, 3).map((task, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 rounded-lg">
                          <div className={`p-1.5 rounded ${task.priority === 'high' ? 'bg-red-100' : 'bg-slate-100'}`}>
                            <Clock className={`h-4 w-4 ${task.priority === 'high' ? 'text-red-500' : 'text-muted-foreground'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{task.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {task.due_date ? format(parseISO(task.due_date), 'MMM d') : 'No due date'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            </SectionShell>
          </div>

          {/* Right Column - Activity & Stats */}
          <div className="flex flex-col gap-6">

            {/* All Services directory */}
            <SectionShell sectionKey="services_directory" column="right" {...shellProps}>
              <ServicesDirectory />
            </SectionShell>

            {/* Business Health Score */}
            <SectionShell sectionKey="business_health" column="right" {...shellProps}>
            <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 border-0 text-foreground">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-medium opacity-90">Business Health</p>
                  <Activity className="h-5 w-5 opacity-75" />
                </div>
                <div className="text-4xl font-bold mb-2">
                  {Math.min(100, Math.round(
                    (dashboardData.conversionRate * 0.4) + 
                    (dashboardData.activeBookings * 5) + 
                    (dashboardData.activeWorkspaces * 3)
                  ))}
                  <span className="text-xl font-normal opacity-75">/100</span>
                </div>
                <Progress 
                  value={Math.min(100, (dashboardData.conversionRate * 0.4) + (dashboardData.activeBookings * 5))} 
                  className="h-2 bg-white/20"
                />
                <p className="text-sm mt-3 opacity-75">
                  Based on conversion rate, active projects, and revenue
                </p>
              </CardContent>
            </Card>
            </SectionShell>

            {/* Recent Bookings */}
            <SectionShell sectionKey="recent_bookings" column="right" {...shellProps}>
            <Card className="bg-card border-border">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-emerald-500" />
                  Recent Bookings
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/pipeline')}>
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {dashboardData.recentBookings.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.recentBookings.slice(0, 4).map((booking, idx) => (
                      <div key={idx} className="flex items-center gap-3" data-testid={`recent-booking-${idx}`}>
                        <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {booking.user_name || 'Customer'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ${(booking.total || 0).toFixed(0)} • {booking.event_type || 'Event'}
                          </p>
                        </div>
                        <Badge variant={booking.status === 'published' || booking.status === 'confirmed' || booking.payment_status === 'paid' ? 'default' : 'outline'}>
                          {booking.status || booking.payment_status || 'pending'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No recent bookings</p>
                )}
              </CardContent>
            </Card>
            </SectionShell>

            {/* Blog & Content */}
            <SectionShell sectionKey="blog_content" column="right" {...shellProps}>
            <Card className="bg-card border-border">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-amber-500" />
                  Blog & Content
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/blog')}>
                  Manage <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 bg-slate-50 dark:bg-card rounded-lg">
                    <p className="text-xl font-bold text-foreground">{dashboardData.publishedPosts}</p>
                    <p className="text-xs text-muted-foreground">Published</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-card rounded-lg">
                    <p className="text-xl font-bold text-foreground">{dashboardData.draftPosts}</p>
                    <p className="text-xs text-muted-foreground">Drafts</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-card rounded-lg">
                    <p className="text-xl font-bold text-foreground">{dashboardData.totalViews}</p>
                    <p className="text-xs text-muted-foreground">Views</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </SectionShell>

            {/* Email Digest Settings */}
            <SectionShell sectionKey="email_digests" column="right" {...shellProps}>
            <Card className="bg-card border-border" data-testid="digest-settings-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-indigo-500" />
                  Email Digests
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Digest Emails</p>
                    <p className="text-xs text-muted-foreground">Receive summary of errors, sales & approvals</p>
                  </div>
                  <button
                    onClick={() => updateDigestSettings({ enabled: !digestSettings.enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${digestSettings.enabled ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-muted'}`}
                    data-testid="digest-toggle"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${digestSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">Frequency:</p>
                  <div className="flex gap-1">
                    {['daily', 'weekly'].map(f => (
                      <button
                        key={f}
                        onClick={() => updateDigestSettings({ frequency: f })}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          digestSettings.frequency === f
                            ? 'bg-indigo-500 text-foreground'
                            : 'bg-slate-100 dark:bg-muted text-muted-foreground hover:bg-accent dark:hover:bg-accent'
                        }`}
                        data-testid={`digest-freq-${f}`}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {digestSettings.last_sent_at && (
                  <p className="text-xs text-muted-foreground">
                    Last sent: {new Date(digestSettings.last_sent_at).toLocaleString()}
                  </p>
                )}

                <Button
                  size="sm"
                  className="w-full bg-indigo-500 hover:bg-indigo-600"
                  onClick={sendDigestNow}
                  disabled={digestSending}
                  data-testid="send-digest-now-btn"
                >
                  {digestSending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                  Send Digest Now
                </Button>
              </CardContent>
            </Card>
            </SectionShell>

            {/* Quick Links */}
            <SectionShell sectionKey="quick_links" column="right" {...shellProps}>
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Manage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { icon: Package, label: 'Products & Services', path: '/admin/products' },
                  { icon: Users, label: 'Vendors', path: '/marketplace' },
                  { icon: Sparkles, label: 'Partner Invitations', path: '/admin/partner-invitations' },
                  { icon: UserPlus, label: 'User Directory', path: '/admin/users' },
                  { icon: UserPlus, label: 'Team & Access', path: '/admin/team' },
                  { icon: UserPlus, label: 'Stakeholders', path: '/admin/stakeholders' },
                  { icon: UserPlus, label: 'EventGoers Bridge', path: '/admin/bridge' },
                  { icon: Settings, label: 'Settings', path: '/admin/settings' },
                ].map((item, idx) => (
                  <Button 
                    key={idx}
                    variant="ghost" 
                    className="w-full justify-start"
                    onClick={() => navigate(item.path)}
                  >
                    <item.icon className="h-4 w-4 mr-3 text-muted-foreground" />
                    {item.label}
                  </Button>
                ))}
              </CardContent>
            </Card>
            </SectionShell>
          </div>
        </div>

      </div>
    </div>
  );
}
